"use client";

import React, { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { Link, useRouter } from "@/i18n/routing";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { resetPassword } from "@/service/auth-service";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

const emailSchema = z.object({
  email: z.string().email("Invalid email address").min(1, "Email is required"),
});

type EmailFormData = z.infer<typeof emailSchema>;

export default function ForgotPasswordPage() {
  const [errorMessage, setErrorMessage] = useState("");
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations("general");
  const { toast } = useToast();

  const form = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: "",
    },
  });

  const { mutate: handleResetPassword, isPending } = useMutation({
    mutationFn: async (data: EmailFormData) => {
      return await resetPassword({ email: data.email });
    },
    onSuccess: () => {
      toast({
        title: t("reset_password") || "Reset Password",
        description:
          t("otp_sent_success") ||
          "Password reset code has been sent to your email. Please check your inbox.",
      });
      // Navigate to OTP verification page with email
      router.push(
        `/auth/forgot-password/verify-otp?email=${encodeURIComponent(
          form.getValues("email")
        )}`
      );
    },
    onError: (error: any) => {
      console.error("Reset password error:", error);
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send reset code. Please try again.";
      setErrorMessage(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: EmailFormData) => {
    setErrorMessage("");
    handleResetPassword(data);
  };

  return (
    <>
      {errorMessage && (
        <div className="bg-red-800 text-center fixed z-50 w-full p-3 flex justify-center">
          <p className="text-white uppercase text-sm font-poppins">
            {errorMessage}
          </p>
        </div>
      )}
      <div className="relative flex justify-center items-center min-h-screen bg-black-bg px-4">
        {/* Background Image */}
        <Image
          src="/signin-bg.webp"
          alt="Background"
          fill
          className="absolute z-10 w-full h-full object-cover"
          priority
        />
        
        {/* Back Button */}
        <Link
          href="/auth/login"
          className={cn(
            "absolute flex justify-center items-center top-5 text-primary-foreground w-12 h-12 p-3 bg-gradient-to-t from-primary to-second-primary rounded-full z-30",
            locale === "ar" ? "right-5" : "left-5"
          )}
        >
          <ArrowLeft className="w-6 h-6" />
        </Link>

        {/* Forgot Password Card */}
        <Card className="mx-auto z-20 border-[#595959] basis-full md:basis-1/2 lg:basis-1/3 bg-black-bg text-primary-foreground rounded-2xl sm:rounded-3xl pb-6 sm:pb-10 pt-8 sm:pt-16 px-4 sm:px-6">
          <CardHeader className="justify-center items-center px-2 sm:px-6">
            <div className="flex justify-center items-center mb-4 sm:mb-6">
              <Image
                src="/logo.webp"
                alt="logo"
                width={160}
                height={160}
                className="w-[120px] sm:w-[160px] object-contain"
              />
            </div>
            <CardTitle className="text-4xl sm:text-5xl lg:text-6xl font-normal">
              <h1 className="lowercase">{t("reset_password") || "reset password"}</h1>
            </CardTitle>
            <CardDescription className="text-primary-foreground font-medium text-sm sm:text-base">
              {t("reset_password_description") ||
                "Enter your email address and we'll send you a code to reset your password."}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            <Form {...form}>
              <form className="grid gap-4" onSubmit={form.handleSubmit(onSubmit)}>
                <div className="grid gap-2">
                  <FormField
                    name="email"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormControl {...field}>
                          <Input
                            id="email"
                            type="email"
                            placeholder={t("email_place_holder") || "Your email address"}
                            required
                            className="bg-black rounded-full border-[#595959] text-primary-foreground py-5 sm:py-6 text-sm sm:text-base"
                          />
                        </FormControl>
                        <FormMessage className="text-start text-red-500" />
                      </FormItem>
                    )}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full font-bebas rounded-full bg-gradient-to-r from-primary to-second-primary text-2xl sm:text-3xl py-5 sm:py-6 sm:pt-7"
                  disabled={isPending}
                >
                  {isPending
                    ? (t("loading") || "Loading...")
                    : (t("continue") || "continue")}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>

      {/* Back to Login Link */}
      <div className="fixed bottom-6 left-0 right-0 z-20 text-center">
        <p className="text-gray-400 font-poppins text-sm">
          {t("back_to_login") || "Remember your password? "}
          <Link
            href="/auth/login"
            className="text-primary hover:underline font-semibold"
          >
            {t("sign_in") || "Sign In"}
          </Link>
        </p>
      </div>
    </>
  );
}
